import { Check, X } from "lucide-react";
import { REGISTER_LIST_DISPLAY_MODES } from "../constants/registerPreferences";
import { getParutionStratumIdForCount } from "../utils/registerParution";
import {
  getDaysSinceLastPlay,
  getRecencyStratumIdForDays,
} from "../utils/registerRecency";
import {
  formatCaptureDateDisplay,
  formatSuccessRatio,
  getGradationBadgeState,
  getLastGameEntry,
  getSuccessRatioTone,
  getWordCorrectCount,
  getWordFailCount,
  getWordGameCount,
} from "../utils/registerWordListDisplay";

function PlaceholderBadge() {
  return <span className="word-list-meta-badge word-list-meta-badge--placeholder" aria-hidden="true" />;
}

function PaquetBadge({ paquet, noyauActif = false }) {
  if (paquet != null && paquet >= 1 && paquet <= 5) {
    return (
      <span className={`word-list-meta-badge word-paquet-badge word-paquet-badge--${paquet}`}>
        Paquet {paquet}
      </span>
    );
  }

  if (noyauActif) {
    return (
      <span className="word-list-meta-badge word-paquet-badge word-paquet-badge--noyau">
        Noyau
      </span>
    );
  }

  return (
    <span className="word-list-meta-badge word-paquet-badge word-paquet-badge--5">
      Paquet 5
    </span>
  );
}

function LastResultBadge({ entry }) {
  const gameCount = getWordGameCount(entry);

  if (gameCount === 0) {
    return (
      <span className="word-list-meta-badge word-list-meta-badge--last-result word-list-meta-badge--last-result-empty">
        —
      </span>
    );
  }

  const last = getLastGameEntry(entry.stats);
  if (!last) {
    return (
      <span className="word-list-meta-badge word-list-meta-badge--last-result word-list-meta-badge--last-result-empty">
        —
      </span>
    );
  }

  const success = last.result === "success";

  return (
    <span
      className={`word-list-meta-badge word-list-meta-badge--last-result${
        success ? " word-list-meta-badge--success" : " word-list-meta-badge--fail"
      }`}
      aria-label={success ? "Dernière parution réussie" : "Dernière parution en échec"}
    >
      {success ? (
        <Check size={12} strokeWidth={2.5} aria-hidden="true" />
      ) : (
        <X size={12} strokeWidth={2.5} aria-hidden="true" />
      )}
    </span>
  );
}

function GradationBadge({ entry }) {
  const { tone, label } = getGradationBadgeState(entry);

  return (
    <span className={`word-list-meta-badge word-list-meta-badge--gradation word-list-meta-badge--gradation-${tone}`}>
      {label}
    </span>
  );
}

function RatioBadge({ entry }) {
  const gameCount = getWordGameCount(entry);
  const correctCount = getWordCorrectCount(entry);
  const tone = getSuccessRatioTone(correctCount, gameCount);

  return (
    <span className={`word-list-meta-badge word-list-meta-badge--ratio word-list-meta-badge--${tone}`}>
      {formatSuccessRatio(correctCount, gameCount)}
    </span>
  );
}

function RecencyDaysBadge({ entry }) {
  const days = getDaysSinceLastPlay(entry);

  if (days == null) {
    return (
      <span className="word-list-meta-badge word-list-meta-badge--recency-days word-list-meta-badge--recency-days-empty">
        —
      </span>
    );
  }

  const stratumId = getRecencyStratumIdForDays(days) || "longAgo";

  return (
    <span
      className={`word-list-meta-badge word-list-meta-badge--recency-days word-list-meta-badge--recency-${stratumId}`}
      aria-label={`${days} jour${days > 1 ? "s" : ""} depuis la dernière apparition`}
    >
      {days} j
    </span>
  );
}

function CaptureDateBadge({ entry }) {
  const label = formatCaptureDateDisplay(entry?.date);

  if (!label) {
    return (
      <span className="word-list-meta-badge word-list-meta-badge--capture-date word-list-meta-badge--capture-date-empty">
        —
      </span>
    );
  }

  return (
    <span
      className="word-list-meta-badge word-list-meta-badge--capture-date"
      aria-label={`Capturé le ${label}`}
    >
      {label}
    </span>
  );
}

function GameCountBadge({ entry }) {
  const gameCount = getWordGameCount(entry);
  const stratumId = getParutionStratumIdForCount(gameCount);

  return (
    <span
      className={`word-list-meta-badge word-list-meta-badge--game-count word-list-meta-badge--recency-${stratumId}`}
      aria-label={`${gameCount} partie${gameCount > 1 ? "s" : ""} jouée${gameCount > 1 ? "s" : ""}`}
    >
      {gameCount}
    </span>
  );
}

function StatsBadge({ entry }) {
  const gameCount = getWordGameCount(entry);

  if (gameCount === 0) {
    return (
      <span className="word-list-meta-badge word-list-meta-badge--stats word-list-meta-badge--stats-never">
        - / - / -
      </span>
    );
  }

  const correctCount = getWordCorrectCount(entry);
  const failCount = getWordFailCount(entry);

  return (
    <span className="word-list-meta-badge word-list-meta-badge--stats word-list-meta-badge--stats-played">
      {gameCount}/{correctCount}/{failCount}
    </span>
  );
}

export default function WordListMetaBadge({ entry, mode }) {
  switch (mode) {
    case REGISTER_LIST_DISPLAY_MODES.LAST_RESULT:
      return <LastResultBadge entry={entry} />;
    case REGISTER_LIST_DISPLAY_MODES.GRADATION:
      return <GradationBadge entry={entry} />;
    case REGISTER_LIST_DISPLAY_MODES.PAQUET:
      return <PaquetBadge paquet={entry.paquet} noyauActif={entry.noyauActif === true} />;
    case REGISTER_LIST_DISPLAY_MODES.RATIO:
      return <RatioBadge entry={entry} />;
    case REGISTER_LIST_DISPLAY_MODES.STATS:
      return <StatsBadge entry={entry} />;
    case REGISTER_LIST_DISPLAY_MODES.RECENCY_DAYS:
      return <RecencyDaysBadge entry={entry} />;
    case REGISTER_LIST_DISPLAY_MODES.GAME_COUNT:
      return <GameCountBadge entry={entry} />;
    case REGISTER_LIST_DISPLAY_MODES.CAPTURE_DATE:
      return <CaptureDateBadge entry={entry} />;
    default:
      return <PaquetBadge paquet={entry.paquet} noyauActif={entry.noyauActif === true} />;
  }
}
